import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getMinisite,
  upsertMinisite,
} from "../../api/minisite";
import EditionTab from "./tabs/EditionTab";
import VersionsTab from "./tabs/VersionsTab";
import ChatTab from "./tabs/ChatTab";
import PublishButton from "./PublishButton";
import ApercuButton from "./ApercuButton";
import HelpLink, { GUIDE_URLS } from "@/components/HelpLink";

const TABS = [
  { id: "edition", label: "Édition" },
  { id: "versions", label: "Versions" },
  { id: "chat", label: "Chat" },
];

export default function MinisiteEditor({ onBackToDashboard }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("edition");
  const [saveStatus, setSaveStatus] = useState("idle");
  const saveTimerRef = useRef(null);

  const fetchDraft = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getMinisite(clientId);
      setDraft(d);
    } catch (e) {
      if (e?.response?.status === 404) {
        setDraft({
          client_id: clientId,
          user_id: "",
          name: "",
          slug: "",
          hero: { name: "", subtitle: "Mini Autobiographie Professionnelle", photo_filename: null },
          adjectifs: [],
          chapters: [],
          interludes: [],
          comments: { enabled: false, notify_email: "" },
          footer: {
            cta_heading: "Envie d'en parler ?",
            cta_text: "",
            contact_type: "phone",
            phone: null,
            phone_display: null,
            email: null,
            website: null,
          },
          cloudflare: {},
        });
      } else {
        setError(e?.response?.data?.detail || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchDraft();
  }, [fetchDraft]);

  const triggerSave = useCallback((next) => {
    setDraft(next);
    setSaveStatus("saving");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const saved = await upsertMinisite(clientId, next);
        setDraft(saved);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 1500);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
        setSaveStatus("idle");
      }
    }, 800);
  }, [clientId]);

  if (loading) return <div style={{ padding: 40 }}>Chargement...</div>;
  if (error) return <div style={{ padding: 40, color: "red" }}>{error}</div>;
  if (!draft) return <div style={{ padding: 40 }}>Pas de draft</div>;

  const handleBackToDashboard = () => {
    if (onBackToDashboard) onBackToDashboard();
    navigate("/");
  };

  return (
    <div className="minisite-module-page" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div className="module-header" style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <button onClick={() => navigate("/minisite")}>← Liste</button>
        <button onClick={handleBackToDashboard} style={{ marginLeft: 8 }}>
          ← Tableau de bord
        </button>
        <h1 className="module-title" style={{ marginLeft: 16 }}>
          {draft.name || "Nouveau minisite"}
        </h1>
        <div className="module-header-actions" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 12, color: "#666" }}>
            {saveStatus === "saving" ? "Sauvegarde..." : saveStatus === "saved" ? "✓ Enregistré" : ""}
          </div>
          <HelpLink url={GUIDE_URLS.minisite} />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <PublishButton clientId={clientId} draft={draft} onPublished={fetchDraft} />
        <ApercuButton clientId={clientId} draft={draft} />
      </div>

      <div style={{ display: "flex", gap: 8, margin: "20px 0", borderBottom: "1px solid #ddd" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              background: activeTab === t.id ? "#000" : "transparent",
              color: activeTab === t.id ? "#fff" : "#000",
              border: "none",
              padding: "8px 16px",
              cursor: "pointer",
              borderRadius: 4,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "edition" && (
        <EditionTab clientId={clientId} draft={draft} onChange={triggerSave} onExtracted={fetchDraft} />
      )}
      {activeTab === "versions" && (
        <VersionsTab clientId={clientId} onRestore={fetchDraft} />
      )}
      {activeTab === "chat" && (
        <ChatTab clientId={clientId} onAgentAction={fetchDraft} />
      )}
    </div>
  );
}
